import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Slide {
  img: string;
  alt: string;
  specialism: string;
  name: string;
  role: string;
  credentials: string[];
  quote: string;
}

const SLIDES: Slide[] = [
  {
    img: '/hussan-mahmood.png',
    alt: 'Sheikh Hussan Mahmood',
    specialism: 'Specialism: Qualified Mufti',
    name: 'Sheikh Hussan Mahmood',
    role: 'Course Lead, Al-Salam Institute',
    credentials: [
      'Course Lead at Al-Salam Institute',
      'Shariah Studies Program at the European Institute for Human Sciences in France and Wales'
    ],
    quote:
      '"We\'ve already reached 200,000 people, and we intend to reach millions in all languages insha\'Allah. This is an app that has been built for Muslims by Muslims to address this communal need."'
  },
  {
    img: '/iqbal-nasim.png',
    alt: 'Iqbal Nasim',
    specialism: 'Specialism: Zakat & Prayer',
    name: 'Iqbal Nasim',
    role: 'CEO, National Zakat Foundation UK',
    credentials: [
      "MBE, Queen's Birthday Honours 2019, for services to tackling poverty and community development",
      "Founding CEO of the National Zakat Foundation UK, the country's leading zakat institution"
    ],
    quote:
      '"This app is a daily guide for those seeking to live a God-centred life. Whether you want to understand the Quran, improve your daily prayers, or increase your knowledge -this app is for you."'
  },
  {
    img: '/ibrahim-khan.png',
    alt: 'Ibrahim Khan',
    specialism: 'Specialism: Halal money & finance',
    name: 'Ibrahim Khan',
    role: 'Co-founder of Cur8 Capital & IFG',
    credentials: [
      'Co-founder of IFG, reaching 4m+ sharia-sensitive investors every month',
      'Leads Cur8 Capital, deploying ~$100m annually into private equity, venture, and real estate funds',
      'Forbes 30 Under 30, Finance (2020)',
      'Oxford-educated barrister and former private equity lawyer in the City'
    ],
    quote:
      '"Millions of Muslims are already asking ChatGPT about their faith. The question isn\'t whether AI will shape how Muslims learn -it\'s whether that AI is built with real Islamic scholarship. Niyyah is the answer."'
  }
];

const Sheikh: React.FC = () => {
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;
  const carouselRef = useRef<HTMLDivElement>(null);

  const go = useCallback(
    (n: number) => setIdx(((n % total) + total) % total),
    [total]
  );

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(idx - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(idx + 1);
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [go, idx]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let tx = 0;
    let ty = 0;
    const onTouchStart = (e: TouchEvent) => {
      tx = e.touches[0].clientX;
      ty = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - tx;
      const dy = e.changedTouches[0].clientY - ty;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(idx + (dx < 0 ? 1 : -1));
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [go, idx]);

  return (
    <section id="sheikh">
      <div className="sheikh-bg-glow" aria-hidden="true" />
      <div className="sheikh-inner">
        <header className="sheikh-header">
          <p className="eyebrow reveal">Trusted content from verified sources</p>
          <h2 className="sheikh-h2 reveal reveal-delay-1">
            Built with <em>scholars and leaders</em> in their fields.
          </h2>
        </header>

        <div
          className="sheikh-carousel reveal reveal-delay-3"
          ref={carouselRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Niyyah scholars and contributors"
        >
          <div className="sheikh-stage">
            {SLIDES.map((slide, i) => {
              const active = i === idx;
              return (
                <article
                  key={i}
                  className={`sheikh-slide${active ? ' is-active' : ''}`}
                  aria-hidden={!active}
                >
                  <div className="sheikh-photo-wrap">
                    <img
                      src={slide.img}
                      alt={slide.alt}
                      className="sheikh-photo-img"
                      width={800}
                      height={1000}
                      loading={i === 0 ? undefined : 'lazy'}
                    />
                  </div>
                  <div className="sheikh-text">
                    <span className="sheikh-specialism">{slide.specialism}</span>
                    <h3 className="sheikh-name">{slide.name}</h3>
                    <p className="sheikh-role">{slide.role}</p>
                    <ul className="sheikh-credentials">
                      {slide.credentials.map((c, j) => (
                        <li key={j}>{c}</li>
                      ))}
                    </ul>
                    <blockquote className="sheikh-pullquote">
                      <p>{slide.quote}</p>
                      <cite>— {slide.name}</cite>
                    </blockquote>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="sheikh-controls">
            <button
              className="sheikh-arrow sheikh-prev"
              type="button"
              aria-label="Previous contributor"
              onClick={() => go(idx - 1)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="sheikh-dots" role="tablist" aria-label="Choose contributor">
              {SLIDES.map((s, i) => {
                const active = i === idx;
                return (
                  <button
                    key={i}
                    className={`sheikh-dot${active ? ' is-active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={s.name}
                    onClick={() => go(i)}
                  />
                );
              })}
            </div>
            <button
              className="sheikh-arrow sheikh-next"
              type="button"
              aria-label="Next contributor"
              onClick={() => go(idx + 1)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M8 4l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <p className="sheikh-more">More scholars and leaders joining throughout the year, in shaa Allah.</p>
        </div>
      </div>
    </section>
  );
};

export default Sheikh;
